import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import RouteLoadingFallback from '../components/RouteLoadingFallback';
import ProtectedRoute from './ProtectedRoute';
import LoginMenuPage from '../pages/LoginMenuPage';

const ConfirmAccountPage = lazy(() => import('../pages/ConfirmAccountPage'));
const DebugPage = lazy(() => import('../pages/DebugPage'));
const DevPreviewVoyageDaySwitcher = lazy(() => import('../pages/_DevPreviewVoyageDaySwitcher'));
const DeclinationCalculatorPage = lazy(() => import('../pages/DeclinationCalculatorPage'));
const BeaufortCalculatorPage = lazy(() => import('../pages/BeaufortCalculatorPage'));
const DouglasCalculatorPage = lazy(() => import('../pages/DouglasCalculatorPage'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const RegisterPage = lazy(() => import('../pages/RegisterPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const SearchPage = lazy(() => import('../pages/SearchPage'));
const ShipLogPage = lazy(() => import('../pages/ShipLogPage'));
const TideCalculatorPage = lazy(() => import('../pages/TideCalculatorPage'));
const UnitCalculatorPage = lazy(() => import('../pages/UnitCalculatorPage'));
const UserPanelPage = lazy(() => import('../pages/userPanel/UserPanelPage'));
const ShipJournalPage = lazy(() => import('../pages/shipJournal/ShipJournalPage'));
const ShipJournalNewPage = lazy(() => import('../pages/shipJournal/wizard/ShipJournalNewPage'));
const ShipJournalNewDiaryWizardPage = lazy(() => import('../pages/shipJournal/wizard/ShipJournalNewDiaryWizardPage'));
const ShipJournalVoyagePage = lazy(() => import('../pages/shipJournal/ShipJournalVoyagePage'));
const MarineLighthouseDecoderPage = lazy(() => import('../pages/MarineLighthouseDecoderPage'));
const ShipLightsPage = lazy(() => import('../pages/ShipLightsPage'));
const ExamPage = lazy(() => import('../pages/ExamPage'));
const GpsTrackerPage = lazy(() => import('../pages/GpsTrackerPage'));
const NamiernikPage = lazy(() => import('../pages/NamiernikPage'));
const SailingGpsLogPage = lazy(() => import('../pages/SailingGpsLogPage'));
const SailingGpsLogWizardNewPage = lazy(() => import('../pages/SailingGpsLogWizardNewPage'));
const UnavailableAsGuestPage = lazy(() => import('../pages/UnavailableAsGuestPage'));
const HomePage = lazy(() => import('../pages/HomePage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const MenuPage = lazy(() => import('../pages/MenuPage'));

function LazyPage({ children }) {
  return <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>;
}

function LegacyTideCalculatorRedirect() {
  const { sectionId } = useParams();
  const nextPath =
    sectionId === 'historia'
      ? '/menu/przeliczniki/kalkulator-plywow/historia'
      : '/menu/przeliczniki/kalkulator-plywow';
  return <Navigate to={nextPath} replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login-menu" element={<LoginMenuPage />} />
      <Route path="/login" element={<LazyPage><LoginPage /></LazyPage>} />
      <Route path="/register" element={<LazyPage><RegisterPage /></LazyPage>} />
      <Route path="/confirm-account/:token" element={<LazyPage><ConfirmAccountPage /></LazyPage>} />
      <Route path="/reset-password/:token" element={<LazyPage><ResetPasswordPage /></LazyPage>} />
      <Route path="/tylko-dla-zalogowanych" element={<LazyPage><UnavailableAsGuestPage /></LazyPage>} />
      <Route path="/debug" element={<LazyPage><DebugPage /></LazyPage>} />
      <Route path="/debug-voyage-day-switcher" element={<LazyPage><DevPreviewVoyageDaySwitcher /></LazyPage>} />
      <Route
        path="/menu/nawigacja/dziennik-pokladowy/nowy"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ShipJournalNewPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/dziennik-pokladowy/nowy-dziennik/:step"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ShipJournalNewDiaryWizardPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/dziennik-pokladowy/rejs/:voyageId/jacht/:yachtId/nowy-dziennik/:step"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ShipJournalNewDiaryWizardPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/dziennik-pokladowy/rejs/:voyageId/nowy-dziennik/:step"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ShipJournalNewDiaryWizardPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/dziennik-pokladowy/rejs/:voyageId"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ShipJournalVoyagePage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/dziennik-pokladowy"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ShipJournalPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/placeholder-namiernik"
        element={
          <ProtectedRoute>
            <LazyPage><NamiernikPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/dekoder-latarni-morskich"
        element={
          <ProtectedRoute>
            <LazyPage><MarineLighthouseDecoderPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/odbiornik-gps"
        element={
          <ProtectedRoute>
            <LazyPage><GpsTrackerPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/log-zeglarski-gps"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><SailingGpsLogPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/log-zeglarski-gps/nowy"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><SailingGpsLogWizardNewPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/nawigacja/log-zeglarski-gps/nowy-dziennik/:step"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ShipJournalNewDiaryWizardPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/log-burtowy"
        element={
          <ProtectedRoute>
            <LazyPage><ShipLogPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/kalkulator-jednostek"
        element={
          <ProtectedRoute>
            <LazyPage><UnitCalculatorPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/kalkulator-deklinacji"
        element={
          <ProtectedRoute>
            <LazyPage><DeclinationCalculatorPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/wskaznik-beauforta/historia"
        element={
          <ProtectedRoute>
            <LazyPage><BeaufortCalculatorPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/wskaznik-beauforta"
        element={
          <ProtectedRoute>
            <LazyPage><BeaufortCalculatorPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/wskaznik-douglasa/historia"
        element={
          <ProtectedRoute>
            <LazyPage><DouglasCalculatorPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/wskaznik-douglasa"
        element={
          <ProtectedRoute>
            <LazyPage><DouglasCalculatorPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/kalkulator-plywow"
        element={
          <ProtectedRoute>
            <LazyPage><TideCalculatorPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/przeliczniki/kalkulator-plywow/historia"
        element={
          <ProtectedRoute>
            <LazyPage><TideCalculatorPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/log-burtowy"
        element={
          <ProtectedRoute>
            <Navigate to="/menu/przeliczniki/log-burtowy" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kalkulator-jednostek"
        element={
          <ProtectedRoute>
            <Navigate to="/menu/przeliczniki/kalkulator-jednostek" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kalkulator-plywow"
        element={
          <ProtectedRoute>
            <Navigate to="/menu/przeliczniki/kalkulator-plywow" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kalkulator-plywow/:sectionId"
        element={
          <ProtectedRoute>
            <LegacyTideCalculatorRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kalkulator-deklinacji"
        element={
          <ProtectedRoute>
            <Navigate to="/menu/przeliczniki/kalkulator-deklinacji" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wskaznik-beauforta"
        element={
          <ProtectedRoute>
            <Navigate to="/menu/przeliczniki/wskaznik-beauforta" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wskaznik-douglasa"
        element={
          <ProtectedRoute>
            <Navigate to="/menu/przeliczniki/wskaznik-douglasa" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/panel-uzytkownika"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><UserPanelPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/panel-uzytkownika/:sectionId"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><UserPanelPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/search"
        element={
          <ProtectedRoute>
            <LazyPage><SearchPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/powiadomienia"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><NotificationsPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/szkolenie/:itemPath/trening"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ShipLightsPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/szkolenie/:itemPath/egzamin"
        element={
          <ProtectedRoute guestAllowed={false}>
            <LazyPage><ExamPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/:categoryId/:itemPath/:subItemPath"
        element={
          <ProtectedRoute>
            <LazyPage><MenuPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/:categoryId/:itemPath"
        element={
          <ProtectedRoute>
            <LazyPage><MenuPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu/:categoryId"
        element={
          <ProtectedRoute>
            <LazyPage><MenuPage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <LazyPage><HomePage /></LazyPage>
          </ProtectedRoute>
        }
      />
      <Route path="/menu" element={<Navigate to="/menu/nawigacja" replace />} />
      <Route path="/" element={<Navigate to="/login-menu" replace />} />
      <Route path="*" element={<Navigate to="/login-menu" replace />} />
    </Routes>
  );
}
